import React, { useEffect, useState } from "react";

const ShippingAndDelivery = () => {
  const [activeSection, setActiveSection] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showHeaderNotice, setShowHeaderNotice] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      setShowHeaderNotice(window.scrollY < 100);

      const sections = document.querySelectorAll("section[id]");
      const scrollMiddle = window.scrollY + window.innerHeight / 2;

      let currentId = "";

      sections.forEach((section) => {
        const el = section as HTMLElement;
        const top = el.offsetTop;
        const height = el.offsetHeight;

        if (scrollMiddle >= top && scrollMiddle < top + height) {
          currentId = el.id;
        }
      });

      // Fallback to last section if scrolled to very bottom
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 5
      ) {
        const lastSection = sections[sections.length - 1];
        currentId = lastSection?.id || currentId;
      }

      if (currentId !== activeSection) {
        setActiveSection(currentId);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const tableOfContents = [
    { id: "shipping", title: "Shipping and Delivery" },
    { id: "delivery", title: "Delivery Method" },
    { id: "timeline", title: "Delivery Timeline" },
    { id: "account", title: "Account Activation & Setup" },
    { id: "access", title: "Access Credentials & Security" },
    { id: "onboarding", title: "On boarding & Support" },
    { id: "non-delivery", title: "Non-Delivery Resolution" },
    { id: "changes", title: "Changes in Service Delivery" },
    { id: "refund", title: "Refunds and Cancellations" },
    { id: "international", title: "International Customers" },
    { id: "contact", title: "Contact Us" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md fixed top-0 left-0 w-full z-50">
        <div className="mx-auto px-6 py-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            MOTSOP AI TECHNOLOGIES PRIVATE LIMITED
          </h1>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Shipping and Delivery Policy
          </h2>
          {/* {showHeaderNotice && (
            <p className="text-gray-600 max-w-6xl mx-auto transition-opacity duration-300">
              This Privacy Policy applies to all Motsop AI Technologies
              Private Limited websites that reference this document, along
              with all software products, applications, and services offered
              through those platforms. The comprehensive policy outlined
              below mirrors the structure of this summary and represents the
              binding legal framework governing our data practices.
            </p>
          )} */}
        </div>
      </header>

      {/* Page Body */}
      <div className="pt-[180px] px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents */}
          <div className="lg:w-1/4">
            <div
              className={`fixed top-[50px]  left-0 w-1/4 max-w-xs bg-white rounded-lg shadow-lg p-6 border h-[calc(100vh-100px)] overflow-y-auto z-60 mt-20`}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`cursor-pointer transition-colors duration-200 w-full text-left px-3 py-2 rounded-md text-sm flex items-center ${
                      activeSection === item.id
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <span className="truncate">{item.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full lg:w-3/4">
            <div className="bg-white rounded-lg shadow-lg border p-8 space-y-8">
              {/* Important Notice */}
              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-amber-800 mb-2">
                  Important Notice
                </h3>
                <p className="text-amber-700">
                  This agreement applies to users based in India. If you are not
                  based in India, our Global Privacy Policy will apply.
                </p>
              </div>

              {/* Section: Cancellation */}
              <section id="shipping" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Shipping and Delivery
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  This Shipping and Delivery Policy outlines how our
                  software-as-a-service (SaaS) products are delivered to
                  customers after purchase. Since our products are digital in
                  nature, there is no physical shipment. Instead, we focus on
                  timely provisioning of accounts, software access, and license
                  keys through secure digital channels.
                </p>
              </section>

              <section id="delivery" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Delivery Method
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Upon successful subscription or purchase of our SaaS products,
                  delivery is conducted digitally through one or more of the
                  following methods:
                </p>
                <li>
                  <b>User Account Access:</b> You will receive an email
                  confirmation with a secure link to create or access your
                  account.
                </li>
                <li>
                  <b>Dashboard Access:</b> The service will be provisioned via
                  our web portal at motsop.com/login
                </li>
                <li>
                  <b>Email Notification:</b> An email with access instructions,
                  on boarding resources, and/or license credentials will be sent
                  to your registered email.
                </li>
                <li>
                  <b>Third-Party Integration</b>(if applicable)<b>:</b> For apps
                  integrated with platforms like Shopify, Slack, or Zoom, access
                  may be provided directly through their app marketplaces.
                </li>
              </section>

              <section id="timeline" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Delivery Timeline
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  The general timelines for service delivery are as follows:
                </p>
                <table className="border border-gray-200 border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-2 border-b">
                        Product Type
                      </th>
                      <th className="text-left px-4 py-2 border-b">
                        Delivery Time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-2 border-b">
                        Self-Serve SaaS Subscriptions
                      </td>
                      <td className="px-4 py-2 border-b">Instant to 1 hour</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">
                        License-based Software
                      </td>
                      <td className="px-4 py-2 border-b">Within 1–12 hours</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 border-b">
                        Custom Onboarding / Enterprise Plans
                      </td>
                      <td className="px-4 py-2 border-b">
                        1–5 business days (based on SLA)
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-gray-700 leading-relaxed">
                  If there is any delay in provisioning your account or access,
                  please contact our support team with your order reference at
                  support@motsop.com
                </p>
              </section>

              <section id="account" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Account Activation &amp; Setup
                </h2>
                <p>To ensure smooth access:</p>
                <p className="text-gray-700 leading-relaxed">
                  You must provide a valid email address during checkout or
                  sign-up.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You will receive an activation email. Follow the instructions
                  to set up your account.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  In case you do not receive the activation email within 1 hour
                  of purchase, please check your spam folder or reach out to
                  support@motsop.com
                </p>
              </section>

              <section id="access" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Access Credentials &amp; Security
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Access credentials (username/password/API keys) are
                  confidential and should not be shared. You are responsible for
                  maintaining the security of your login credentials. If you
                  suspect unauthorized access, notify us immediately so we can
                  temporarily disable your account.
                </p>
              </section>

              <section id="onboarding" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  On boarding &amp; Support
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Our standard on boarding materials are delivered via email or
                  in-app walkthroughs. For enterprise users, a Customer Success
                  Manager may schedule on boarding sessions.
                </p>
              </section>
              <section id="non-delivery" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Non-Delivery Resolution
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you do not receive access to the SaaS product within the
                  promised delivery time:
                </p>
                <li>Verify the email address you used for registration.</li>
                <li>Check your spam or junk mail folder.</li>
                <li>
                  Contact our support team via-<b>Email: </b>
                  <a className="text-[blue]" href="mailto:support@motsop.com">
                    support@motsop.com
                  </a>
                </li>
                <li>
                  Our team will investigate and ensure resolution within 24
                  hours.
                </li>
              </section>
              <section id="changes" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Changes in Service Delivery
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We may occasionally modify or upgrade our software delivery
                  infrastructure. In such cases you will be notified in advance
                  via email. Your access will not be interrupted during
                  scheduled maintenance, unless specified.
                </p>
              </section>
              <section id="refund" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Refunds and Cancellations
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Delivery of the SaaS product is considered complete once
                  access has been granted or credentials issued.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  For refund and cancellation terms, please refer to our Refund
                  Policy at motsop.com/refund- policy
                </p>
              </section>

              <section id="international" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  International Customers
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We serve clients worldwide. All delivery policies apply
                  universally, but please allow for time zone differences in
                  support responses.
                </p>
              </section>

              <section id="contact" className="scroll-mt-[100px]">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                  Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  For any questions regarding delivery, onboarding, or account
                  access, please reach out to:
                </p>
                <b>Email: </b>
                <a className="text-[blue]" href="mailto:support@motsop.com">
                  support@motsop.com
                </a>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default ShippingAndDelivery;
